import { OfetchHttpClient } from '../http/OfetchHttpClient';
import { type BffEndpoint } from '../endpoints/BffEndpoints';
import { ApiClient } from './ApiClient';

export interface BffApiClientConfig {
  baseUrl: string;
  tenantId?: string;
}

/**
 * Public BFF (Backend For Frontend) API client.
 * Handles all communication with the BFF gateway for public/unauthenticated endpoints.
 * This client does NOT expose authentication methods and never sends auth tokens.
 * Type-safe: Only allows endpoints defined in BFF_ENDPOINTS.
 */
export class BffApiClient extends ApiClient<BffEndpoint> {
  private static instance: BffApiClient | null = null;
  private static config: BffApiClientConfig | null = null;

  protected constructor(config: BffApiClientConfig) {
    const headers = BffApiClient.getDefaultHeaders(config.tenantId);
    const httpClient = new OfetchHttpClient(config.baseUrl, headers);

    super(httpClient, { version: 'v1' });
  }

  /**
   * Initialize the API client with configuration
   */
  static initialize(config: BffApiClientConfig): void {
    BffApiClient.config = config;
    BffApiClient.instance = null; // Reset instance to use new config
  }

  /**
   * Get singleton instance of Public BFF API client
   */
  static getInstance(): BffApiClient {
    if (!BffApiClient.config) {
      throw new Error('BffApiClient must be initialized with configuration before use. Call BffApiClient.initialize() first.');
    }

    BffApiClient.instance ||= new BffApiClient(BffApiClient.config);
    return BffApiClient.instance;
  }

  /**
   * Reset both instance and configuration (useful for testing initialization)
   */
  static resetInstance(): void {
    BffApiClient.instance = null;
    BffApiClient.config = null;
  }

  /**
   * Get the current configuration (useful for extending classes)
   */
  static getConfig(): BffApiClientConfig {
    if (!BffApiClient.config) {
      throw new Error('BffApiClient must be initialized with configuration before use. Call BffApiClient.initialize() first.');
    }
    return BffApiClient.config;
  }

  protected static getDefaultHeaders(tenantId?: string): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (tenantId) {
      headers['X-Tenant-Id'] = tenantId.toLowerCase(); // BFF expects lowercase
    }

    return headers;
  }

  // Note: This class intentionally does NOT expose setAuthToken/clearAuthToken methods
  // to prevent authentication on public endpoints
}
